import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Users, Syringe, Baby, AlertTriangle, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { useSettings } from '@/contexts/SettingsContext';
import { BirthsChart, StockConsumptionChart, HerdEvolutionChart, RevenueExpenseChart, SurvivalRateChart } from '@/components/dashboard/DashboardCharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { settings } = useSettings();
  const chartPeriod = settings.chart_period_months || 12;
  const currency = settings.currency_symbol || 'DT';

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchCharts();
  }, [chartPeriod]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const fetchCharts = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/charts?months=${chartPeriod}`);
      setChartData(response.data.data || []);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="spinner"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Troupeau actif',
      value: stats?.total_sheep || 0,
      icon: Users,
      bgColor: 'bg-slate-100 dark:bg-slate-800',
      iconColor: 'text-slate-700 dark:text-slate-300',
      link: '/troupeau',
      description: 'Voir tous les moutons'
    },
    {
      title: 'Vaccins à venir',
      value: stats?.upcoming_vaccinations || 0,
      icon: Syringe,
      bgColor: 'bg-blue-100 dark:bg-blue-950',
      iconColor: 'text-blue-600 dark:text-blue-400',
      link: '/troupeau',
      description: 'Gérer les vaccinations'
    },
    {
      title: 'Naissances prévues',
      value: stats?.upcoming_births || 0,
      icon: Baby,
      bgColor: 'bg-pink-100 dark:bg-pink-950',
      iconColor: 'text-pink-600 dark:text-pink-400',
      link: '/naissances',
      description: 'Gérer les naissances'
    },
    {
      title: 'Alertes stock',
      value: stats?.low_stock_alerts || 0,
      icon: AlertTriangle,
      bgColor: 'bg-orange-100 dark:bg-orange-950',
      iconColor: 'text-orange-600 dark:text-orange-400',
      link: '/stock',
      description: 'Voir le stock'
    },
    {
      title: 'Transactions (7j)',
      value: stats?.recent_transactions || 0,
      icon: TrendingUp,
      bgColor: 'bg-violet-100 dark:bg-violet-950',
      iconColor: 'text-violet-600 dark:text-violet-400',
      link: '/transactions',
      description: 'Voir les transactions'
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Tableau de bord</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Bienvenue, {user?.nom}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Link key={index} to={stat.link} className="block">
              <Card className="card-hover border-0 shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer" data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{stat.title}</p>
                      <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stat.value}</p>
                      <p className="text-xs text-gray-500 mt-1">{stat.description}</p>
                    </div>
                    <div className={`${stat.bgColor} p-3 rounded-xl`}>
                      <Icon className={`w-6 h-6 ${stat.iconColor}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Charts Section */}
      {chartData.length > 0 && (
        <div className="space-y-6" data-testid="dashboard-charts">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Évolution sur {chartPeriod} mois</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RevenueExpenseChart data={chartData} currency={currency} />
            <HerdEvolutionChart data={chartData} />
            <BirthsChart data={chartData} />
            <StockConsumptionChart data={chartData} />
          </div>
          <SurvivalRateChart data={chartData} />
        </div>
      )}

      {/* Upcoming Vaccinations */}
      {stats?.vaccinations_details && stats.vaccinations_details.length > 0 && (
        <Card className="border-0 shadow-md">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl flex items-center space-x-2">
                <Syringe className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <span>Vaccinations à venir (30 prochains jours)</span>
              </CardTitle>
              <Link 
                to="/troupeau"
                className="text-sm text-blue-600 hover:text-blue-800 font-medium hover:underline"
              >
                Voir tout le troupeau →
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.vaccinations_details.slice(0, 5).map((vacc) => (
                <div key={vacc.id} className="flex items-center justify-between p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-gray-900 dark:text-white">{vacc.nom}</p>
                      <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded-full">
                        #{vacc.numero || 'N/A'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Vaccin:</span> {vacc.type}
                    </p>
                    {vacc.sexe && (
                      <p className="text-xs text-gray-500 mt-1">
                        <span className="font-medium">Sexe:</span> {vacc.sexe === 'male' ? '♂️ Mâle' : '♀️ Femelle'}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-blue-600">
                      {new Date(vacc.prochaine_date).toLocaleDateString('fr-FR')}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Prochaine vaccination
                    </p>
                  </div>
                </div>
              ))}
              {stats.vaccinations_details.length > 5 && (
                <div className="text-center pt-2">
                  <Link
                    to="/troupeau"
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium hover:underline"
                  >
                    Voir {stats.vaccinations_details.length - 5} autres vaccinations →
                  </Link>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upcoming Births */}
      {stats?.births_details && stats.births_details.length > 0 && (
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-xl flex items-center space-x-2">
              <Baby className="w-5 h-5 text-pink-600 dark:text-pink-400" />
              <span>Naissances prévues (30 prochains jours)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.births_details.map((birth) => (
                <div key={birth.id} className="flex items-center justify-between p-4 bg-pink-50 rounded-lg hover:bg-pink-100 transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-gray-900 dark:text-white">🐑 {birth.mere_nom}</p>
                      <span className="text-xs bg-pink-200 text-pink-800 px-2 py-1 rounded-full">
                        N° {birth.mere_numero}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Statut:</span> {birth.statut === 'prevue' ? '⏳ Prévue' : birth.statut}
                    </p>
                    {birth.notes && (
                      <p className="text-xs text-gray-500 mt-1">
                        📝 {birth.notes}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-pink-600">
                      {new Date(birth.date_prevue).toLocaleDateString('fr-FR')}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Date prévue
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Low Stock Alerts */}
      {stats?.stock_alerts_details && stats.stock_alerts_details.length > 0 && (
        <Card className="border-0 shadow-md border-orange-200">
          <CardHeader>
            <CardTitle className="text-xl flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              <span>Alertes de stock faible</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.stock_alerts_details.map((stock) => (
                <div key={stock.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{stock.type}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Seuil: {stock.seuil_alerte} {stock.unite}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-orange-600">
                      {stock.quantite} {stock.unite}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;